import { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import { Table } from 'components/UI/V3/Table/Table';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { PaginationProps } from 'components/UI/V3/Pagination/Pagination';
import { VaccineApproval, VaccineApprovalDetailMode } from 'model/VaccineApproval';
import VaccineApprovalsTableRow from './VaccineApprovalsTableRow';
import VaccineApprovalsIsEmpty from './VaccineApprovalsIsEmpty';
import styles from './VaccineApprovalsTable.module.scss';

interface VaccineApprovalsTableProps {
    items: VaccineApproval[];
    timeZone: string;
    selectedTab: 'pending' | 'approved' | 'declined' | 'all';
    pagination: PaginationProps;
    onPageChange: (page: number) => void;
    onSizeChange: (size: number) => void;
    onOpenDetail: (item: VaccineApproval, mode: VaccineApprovalDetailMode) => void;
    className?: string;
}

const VaccineApprovalsTable: FunctionComponent<VaccineApprovalsTableProps> = ({
    items,
    timeZone,
    selectedTab,
    pagination,
    onPageChange,
    onSizeChange,
    onOpenDetail,
    className
}) => {
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });
    const isTabletOrBelow = useMediaQuery({ query: '(max-width:960px)' });
    const colSpan = 6;
    const isEmpty = items.length === 0;
    const showHeader = !isMobile || !isEmpty;

    const renderTableContent = () => {
        if (isEmpty) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={colSpan}
                            className={styles.emptyCell}
                        >
                            <div className={styles.emptyCellContent}>
                                <VaccineApprovalsIsEmpty selectedTab={selectedTab} />
                            </div>
                        </td>
                    </tr>
                </TableBody>
            );
        }

        return (
            <TableBody>
                {items.map((item, index) => (
                    <VaccineApprovalsTableRow
                        key={item.id}
                        item={item}
                        timeZone={timeZone}
                        isLast={items.length === index + 1}
                        onOpenDetail={onOpenDetail}
                    />
                ))}
            </TableBody>
        );
    };

    return (
        <Table
            className={[styles.table, className].filter(Boolean).join(' ')}
            fullHeight={true}
            firstColumnFixed={isTabletOrBelow}
            pagination={pagination}
            onPageChange={onPageChange}
            onSizeChange={onSizeChange}
        >
            {showHeader && (
                <TableHeader className={styles.tableHeader}>
                    <TableHeaderCell>Date</TableHeaderCell>
                    <TableHeaderCell>Client</TableHeaderCell>
                    <TableHeaderCell>Pet Name</TableHeaderCell>
                    <TableHeaderCell>Vaccine</TableHeaderCell>
                    <TableHeaderCell>Expiration</TableHeaderCell>
                    <TableHeaderCell></TableHeaderCell>
                </TableHeader>
            )}
            {renderTableContent()}
        </Table>
    );
};

export default VaccineApprovalsTable;
